import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { api, fileUrl } from "@/lib/api";
import { Trash2, ShoppingBag, MapPin } from "lucide-react";
import { toast } from "sonner";

function img(p) {
  if (!p) return "https://static.prod-images.emergentagent.com/jobs/d3a3c830-7d3a-49ef-ad18-3f342dc4beda/images/61fe03154047828097ebbc7961c5f590c8897894b9ea6fa9e207bb0d1fee5801.png";
  if (typeof p === "string" && p.startsWith("http")) return p;
  return fileUrl(p);
}

const FIELDS = [
  { key: "name", label: "Full name", w: "sm:col-span-2" },
  { key: "line1", label: "Address line 1", w: "sm:col-span-2" },
  { key: "line2", label: "Apt / suite (optional)", w: "sm:col-span-2", optional: true },
  { key: "city", label: "City" },
  { key: "region", label: "State / region", optional: true },
  { key: "postal", label: "ZIP / postal" },
  { key: "country", label: "Country" },
  { key: "phone", label: "Phone (optional)", w: "sm:col-span-2", optional: true },
];

export default function Cart() {
  const { user } = useAuth();
  const { items, total, remove, refresh } = useCart();
  const [paying, setPaying] = useState(false);
  const [ship, setShip] = useState({ name: "", line1: "", line2: "", city: "", region: "", postal: "", country: "United States", phone: "" });
  const nav = useNavigate();

  if (!user) return <div className="pt-32 text-center" data-testid="cart-need-login"><p className="text-zinc-400 mb-4">Sign in to view your cart.</p><Link to="/login?next=/cart" className="btn-primary">Sign in</Link></div>;

  const required = ["name", "line1", "city", "postal", "country"];
  const missing = required.filter(k => !ship[k] || !ship[k].trim());

  const checkout = async () => {
    if (items.length === 0) return;
    if (missing.length > 0) { toast.error("Please complete the shipping address"); return; }
    setPaying(true);
    try {
      const { data } = await api.post("/checkout/cart", {
        origin_url: window.location.origin,
        shipping_address: { ...ship, line2: ship.line2 || null, region: ship.region || null, phone: ship.phone || null },
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Checkout failed");
      setPaying(false);
    }
  };

  return (
    <div data-testid="cart-page" className="pt-28 pb-24 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-10">Your cart</h1>
      {items.length === 0 ? (
        <div data-testid="cart-empty" className="card-surface p-12 text-center">
          <ShoppingBag className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-400">Your cart is empty.</p>
          <Link to="/browse" className="btn-secondary inline-block mt-6">Browse products</Link>
        </div>
      ) : (
        <>
          <ul className="divide-y divide-white/5 border border-white/5">
            {items.map((it) => (
              <li key={it.product.id} className="flex items-center gap-4 p-4">
                <img src={img(it.product.images?.[0])} alt="" className="w-20 h-20 object-cover" />
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${it.product.id}`} className="font-bold hover:text-[#FF007F]" data-testid={`cart-item-title-${it.product.id}`}>{it.product.title}</Link>
                  <p className="text-xs text-zinc-500 uppercase tracking-widest mt-1">qty {it.quantity}</p>
                </div>
                <div className="font-display font-bold text-[#FF007F]">${(it.product.price * it.quantity).toFixed(2)}</div>
                <button data-testid={`cart-remove-${it.product.id}`} onClick={async () => { await remove(it.product.id); refresh(); }} className="p-2 text-zinc-500 hover:text-red-400">
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>

          {/* Shipping address */}
          <div className="mt-10 card-surface p-6">
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-6 flex items-center gap-2"><MapPin className="w-3 h-3" /> Shipping address</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {FIELDS.map(f => (
                <div key={f.key} className={f.w || ""}>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500">{f.label}</label>
                  <input
                    data-testid={`ship-${f.key}`}
                    value={ship[f.key]}
                    onChange={(e) => setShip({ ...ship, [f.key]: e.target.value })}
                    className="input-dark mt-2"
                    required={!f.optional}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-between items-end mt-8">
            <p className="text-xs uppercase tracking-widest text-zinc-500">Subtotal</p>
            <p className="font-display font-black text-3xl text-[#FF007F]" data-testid="cart-total">${total.toFixed(2)}</p>
          </div>
          <div className="flex justify-end mt-6">
            <button data-testid="checkout-btn" onClick={checkout} disabled={paying || missing.length > 0} className="btn-primary">
              {paying ? "Redirecting…" : missing.length > 0 ? "Complete shipping above" : "Proceed to checkout"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
